import React, { useState, useEffect, useMemo } from 'react';
import { View, ScrollView, LogBox } from 'react-native';
import { interpolateBlues, schemeCategory10 } from 'd3-scale-chromatic'
import LineChartScreen from '../common/charts/lineChart-builder';
import { colorType3, colorType5, logStyles } from './LogStyles';
import PaperTableDynamic from '../common/controls/TableProvider';
import PieChartComp from '../common/charts/piChart-builder';
import { findImmediateParent, getAllItems, getItemById } from '../common/service/dataOperations';
import { useAuth } from '../../../providers/AuthProvider';
import { useClient } from '../../../providers/ClientProvider';
import { scaleOrdinal } from 'd3-scale';
import CustomDT from '../common/controls/customDataTable';
import { Loader } from '../../Common/Loader';
import { TabScreen, Tabs, TabsProvider } from 'react-native-paper-tabs';
import LogKpiRow from './LogKpiRow';
import LogTypeBreakdown from './LogTypeBreakdown';
import LogDateRangeFilter from '../common/controls/LogDateRangeFilter';
import { useNav } from '../../../providers/NavProvider';

interface Props {
  siteId: string;
  logTitle: string;
}

export default function WasteWaterLog(props: Props) {
  LogBox.ignoreLogs(["EventEmitter.removeListener"]);
  const [wasteWaterLogData, setWasteWaterLogData] = useState<null | any>(null);
  const [wasteWaterLogColumns, setWasteWaterLogColumns] = useState<null | any>(null);
  const { getAccessToken } = useAuth();
  const { clientAPIURL } = useClient();
  const { sitesResp } = useNav();

  useEffect(() => {
    async function fetchData() {
      try {
        const packageSite = sitesResp && findImmediateParent(sitesResp, props.siteId);
        if (packageSite && packageSite.id) {
          const accessToken = await getAccessToken();
          const json = await getAllItems(props.logTitle, accessToken, clientAPIURL, props.siteId, undefined, packageSite.id);
          setWasteWaterLogData(json.items);
          setWasteWaterLogColumns(json.columns);
        }
      } catch (error) {
        console.error(error);
      }
    }
    sitesResp && fetchData();
  }, [sitesResp]);

  return wasteWaterLogData && wasteWaterLogColumns
    ? <CreateLogCharts wasteWaterLogData={wasteWaterLogData} wasteWaterLogColumns={wasteWaterLogColumns} siteId={props.siteId} logTitle={props.logTitle} clientAPIURL={clientAPIURL} />
    : <Loader />;
}

interface CreateLogChartsProps {
  wasteWaterLogData: any[];
  wasteWaterLogColumns: any;
  siteId: string;
  logTitle: string;
  clientAPIURL: string;
}

function returnDate(value: any): any {
  if (!value) return value;
  const date = new Date(value);
  const adjusted = new Date(date.getTime() + 4 * 60 * 60000);
  const d = adjusted.getDate();
  const m = adjusted.getMonth() + 1;
  const y = adjusted.getFullYear();
  return `${d < 10 ? '0' + d : d}/${m < 10 ? '0' + m : m}/${y}`;
}

const CreateLogCharts: React.FC<CreateLogChartsProps> = ({ wasteWaterLogData, wasteWaterLogColumns, siteId, logTitle, clientAPIURL }) => {
  const { currentLeafObj } = useNav();
  const defaultTab = currentLeafObj.permissions && JSON.stringify(currentLeafObj.permissions).toLowerCase().includes('write') ? 'list' : 'chart';
  const { getAccessToken } = useAuth();

  const ColumnsWithIDColumn = wasteWaterLogColumns && [
    ...wasteWaterLogColumns,
    {
      id: 'CODE_GENERATED_ID',
      title: 'Item ID',
      columnName: 'ID',
      list: '7ae860e9-6c67-46d0-b65e-f2869542a3aa',
      datatype: 'calculated',
      createdAt: 'YOUR_CREATED_AT_TIMESTAMP',
      modifiedAt: 'YOUR_MODIFIED_AT_TIMESTAMP',
      required: false,
      subType: 'Float',
      choiceChoices: null,
      choiceType: null,
      choiceMultiselect: null,
      multiline: false,
      order: 0,
      calculatedFormula: null,
      listNavigation: null,
    },
  ];

  const [filteredData, setFilteredData] = useState<any[]>(wasteWaterLogData ?? []);

  async function handleDataUpdate(item: any) {
    if (item.ID) {
      try {
        const accessToken = await getAccessToken();
        const result = await getItemById(logTitle, accessToken, clientAPIURL, siteId, item.ID);
        const updated = result.items[0];
        if (!updated) return;
        const formattedDate = returnDate(updated.Date);
        const newItem = { ...updated, Date: formattedDate };
        setFilteredData(prev => [...prev.filter((o: any) => o.ID !== item.ID), newItem]);
      } catch (ex) {
        console.error(ex);
      }
    }
  }

  // LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=(opacity)=>string
  // PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string
  const chartData = useMemo(() => {
    const uniquePeriod = [...new Set(filteredData.map(item => item.Period).filter(Boolean))] as string[];

    // Quantity-filtered subset for line charts (only records with a valid Quantitym3)
    const quantityFiltered = filteredData.filter(
      item => item.Quantitym3 !== null && item.Quantitym3 !== ''
    );

    // --- Pie: Wastewater by Type (sum of Quantitym3) ---
    const pieByType: Record<string, number> = {};
    filteredData.forEach((item: any) => {
      if (item.WastewaterType == null) return;
      const qty = Number(item.Quantitym3) || 0;
      pieByType[String(item.WastewaterType)] = (pieByType[String(item.WastewaterType)] ?? 0) + qty;
    });
    const byWasteWaterTypePieData = Object.keys(pieByType).map((name, index) => ({
      name,
      value: Math.round(pieByType[name]),
      color: colorType5(index.toString()),
    }));

    // --- Pie: Wastewater by Destination/DisposalMethod (sum of Quantitym3) ---
    const pieByDisposal: Record<string, number> = {};
    filteredData.forEach((item: any) => {
      if (item.DisposalMethod == null) return;
      const qty = Number(item.Quantitym3) || 0;
      pieByDisposal[String(item.DisposalMethod)] = (pieByDisposal[String(item.DisposalMethod)] ?? 0) + qty;
    });
    const byWasteWaterDestinationPieData = Object.keys(pieByDisposal).map((name, index) => ({
      name,
      value: Math.round(pieByDisposal[name]),
      color: colorType3(index.toString()),
    }));

    // --- Line: Qty by DisposalMethod per Period ---
    const uniqueDisposalMethods = [...new Set(quantityFiltered.map(item => item.DisposalMethod))].filter((m): m is string => m != null);
    const lineChartData: Record<string, number[]> = {};
    uniqueDisposalMethods.forEach(method => { lineChartData[method] = []; });
    uniquePeriod.forEach(period => {
      uniqueDisposalMethods.forEach(method => {
        const sum = quantityFiltered
          .filter(item => item.Period === period && item.DisposalMethod === method)
          .reduce((acc, item) => acc + (Number(item.Quantitym3) || 0), 0);
        lineChartData[method].push(sum);
      });
    });
    const colorScaleLine = scaleOrdinal(schemeCategory10);
    const lineDatasets = Object.entries(lineChartData).map(([, values], index) => ({
      data: values,
      strokeWidth: 2,
      color: (opacity = 1) => colorScaleLine(index.toString()) + Math.round(opacity * 255).toString(16).padStart(2, '0'),
    }));
    const lineData = { labels: uniquePeriod, datasets: lineDatasets };
    const legendData2 = uniqueDisposalMethods.map(name => ({ name }));

    // --- Line: Qty by WastewaterType per Period ---
    const uniqueWasteWaterType = [...new Set(quantityFiltered.map(item => item.WastewaterType))].filter((t): t is string => t != null);
    const lineChartDataByType: Record<string, number[]> = {};
    uniqueWasteWaterType.forEach(type => { lineChartDataByType[type] = []; });
    uniquePeriod.forEach(period => {
      uniqueWasteWaterType.forEach(type => {
        const sum = quantityFiltered
          .filter(item => item.Period === period && item.WastewaterType === type)
          .reduce((acc, item) => acc + (Number(item.Quantitym3) || 0), 0);
        lineChartDataByType[type].push(sum);
      });
    });
    const colorScaleLineType = scaleOrdinal(schemeCategory10);
    const lineDatasetsByType = Object.entries(lineChartDataByType).map(([, values], index) => ({
      data: values,
      strokeWidth: 2,
      color: (opacity = 1) => colorScaleLineType(index.toString()) + Math.round(opacity * 255).toString(16).padStart(2, '0'),
    }));
    const lineDataByType = { labels: uniquePeriod, datasets: lineDatasetsByType };
    const legendData1 = uniqueWasteWaterType.map(name => ({ name }));

    // --- Table 1: Qty by WastewaterType per Period ---
    const wasteWaterTypeChoices = wasteWaterLogColumns
      .find((col: any) => col.columnName === 'WastewaterType')?.choiceChoices;
    const wasteWaterTypesTable: string[] = wasteWaterTypeChoices
      ? wasteWaterTypeChoices.split('#;#').map((v: string) => v.split('#;#')[0])
      : [];

    const groupedByType = filteredData.reduce((result: any, item: any) => {
      const { Period, WastewaterType, Quantitym3 } = item;
      if (!result[Period]) {
        result[Period] = { Period };
        wasteWaterTypesTable.forEach((t: string) => { result[Period][t] = 0; });
      }
      if (WastewaterType != null) {
        result[Period][String(WastewaterType)] = (result[Period][String(WastewaterType)] ?? 0) + Math.round(Number(Quantitym3) || 0);
      }
      return result;
    }, {});
    const mergedData = Object.values(groupedByType).map((values: any) => {
      const sum = wasteWaterTypesTable.reduce((s: number, t: string) => s + (values[t] ?? 0), 0);
      return { ...values, 'Total (By Wastewater Type)': sum };
    });
    const columnKeys = mergedData.length > 0
      ? Object.keys(mergedData[0]).map((key, index) => ({ ColumnName: key, Order: index }))
      : [];

    // --- Table 2: Qty by DisposalMethod per Period ---
    const disposalMethodChoices = wasteWaterLogColumns
      .find((col: any) => col.columnName === 'DisposalMethod')?.choiceChoices;
    const wasteWaterDisposalMethodTable: string[] = disposalMethodChoices
      ? disposalMethodChoices.split('#;#').map((v: string) => v.split('#;#')[0])
      : [];

    const groupedByDisposal = filteredData.reduce((result: any, item: any) => {
      const { Period, DisposalMethod, Quantitym3 } = item;
      if (!result[Period]) {
        result[Period] = { Period };
        wasteWaterDisposalMethodTable.forEach((d: string) => { result[Period][d] = 0; });
      }
      if (DisposalMethod != null) {
        result[Period][String(DisposalMethod)] = (result[Period][String(DisposalMethod)] ?? 0) + Math.round(Number(Quantitym3) || 0);
      }
      return result;
    }, {});
    const mergedData2 = Object.values(groupedByDisposal).map((values: any) => {
      const sum = wasteWaterDisposalMethodTable.reduce((s: number, d: string) => s + (values[d] ?? 0), 0);
      return { ...values, 'Total (By Disposal Method)': sum };
    });
    const columnKeys2 = mergedData2.length > 0
      ? Object.keys(mergedData2[0]).map((key, index) => ({ ColumnName: key, Order: index }))
      : [];

    // --- KPI totals ---
    const totalCount = filteredData.reduce((total, item) => total + (Number(item.Quantitym3) || 0), 0);

    return {
      byWasteWaterTypePieData,
      byWasteWaterDestinationPieData,
      lineData,
      legendData2,
      lineDataByType,
      legendData1,
      uniquePeriod,
      mergedData,
      columnKeys,
      mergedData2,
      columnKeys2,
      totalCount,
    };
  }, [filteredData, wasteWaterLogColumns]);

  const kpiItems = [
    { label: 'Total Volume (m3)', value: Math.round(chartData.totalCount), color: '#00695C' },
    { label: 'Entries', value: filteredData.length, color: '#4527A0' },
    ...chartData.byWasteWaterTypePieData.map(d => ({ label: d.name, value: d.value, color: d.color, unit: 'm³' })),
  ];

  return (
    <View style={logStyles.logRoot}>
      <TabsProvider defaultIndex={defaultTab === 'list' ? 2 : 0}>
        <Tabs style={{ backgroundColor: '#fff', height: '100%' }} showLeadingSpace={true}>

          <TabScreen label="Charts" icon="chart-bar">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View style={{ zIndex: 200 }}>
                <LogKpiRow items={kpiItems}>
                  <LogDateRangeFilter data={wasteWaterLogData} onFilterChange={setFilteredData} />
                </LogKpiRow>
              </View>
              <LogTypeBreakdown data={filteredData} columns={wasteWaterLogColumns} typeField="DisposalMethod" valueField="Quantitym3" unit="m³" />
              <View>
                <View style={logStyles.container}>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <PieChartComp data={chartData.byWasteWaterTypePieData} colorPallate={interpolateBlues} subTitle={'(Cumulative - m3)'} ChartTitle={"Wastewater Qty/Type"} />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <LineChartScreen legend={chartData.legendData1} data={chartData.lineDataByType} label={chartData.uniquePeriod} subTitle={'(Monthly - m3)'} ChartTitle={"Wastewater Qty/Type"} />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <PieChartComp data={chartData.byWasteWaterDestinationPieData} colorPallate={interpolateBlues} subTitle={'(Cumulative - m3)'} ChartTitle={"Wastewater Qty/Destination"} />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <LineChartScreen legend={chartData.legendData2} data={chartData.lineData} label={chartData.uniquePeriod} subTitle={'(Monthly - m3)'} ChartTitle={"Wastewater Qty/Destination"} />
                  </View>
                </View>
              </View>
            </ScrollView>
          </TabScreen>

          <TabScreen label="Tables" icon="table">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <LogTypeBreakdown data={filteredData} columns={wasteWaterLogColumns} typeField="DisposalMethod" valueField="Quantitym3" unit="m³" />
              <View>
                <View style={logStyles.container}>
                  <View style={logStyles.cardThree}>
                    {chartData.mergedData && chartData.columnKeys ? (
                      <CustomDT
                        data={chartData.mergedData}
                        columns={chartData.columnKeys}
                        siteId={siteId}
                        logTitle={logTitle}
                        tableId={'WasteWaterByUse'}
                        tableTitle={"Wastewater Quantities (m3) - By Use"}
                      />
                    ) : null}
                  </View>
                  <View style={logStyles.cardThree}>
                    {chartData.mergedData2 && chartData.columnKeys2 ? (
                      <CustomDT
                        data={chartData.mergedData2}
                        columns={chartData.columnKeys2}
                        siteId={siteId}
                        logTitle={logTitle}
                        tableId={'WasteWaterByDest'}
                        tableTitle={"Wastewater Quantities (m3) - By Destination"}
                      />
                    ) : null}
                  </View>
                </View>
              </View>
            </ScrollView>
          </TabScreen>

          <TabScreen label="List" icon="database">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View>
                <View style={logStyles.container}>
                  <View style={logStyles.cardThree}>
                    {setFilteredData && filteredData && ColumnsWithIDColumn && (
                      <PaperTableDynamic
                        data={filteredData}
                        columns={ColumnsWithIDColumn}
                        siteId={siteId}
                        logTitle={logTitle}
                        updateData={handleDataUpdate}
                        isConsolidatedTable={false}
                        funcSetShowFilter={() => {}}
                      />
                    )}
                  </View>
                </View>
              </View>
            </ScrollView>
          </TabScreen>

        </Tabs>
      </TabsProvider>
    </View>
  );
};
